// filepath: /Users/raymartosido/Documents/Shopify Projects/the-modern-furniture-store/assets/collection-load-more.js
class CollectionLoadMore {
  constructor() {
    this.loadMoreBtn = document.getElementById('load-more-btn');
    this.productsContainer = document.getElementById('collection-products-container');
    this.productList = this.productsContainer?.querySelector('product-list');
    
    // Always check if we need to load previous pages on initial load
    this.handleInitialPageLoad();
    
    if (this.loadMoreBtn) {
      this.init();
    }
  }

  init() {
    this.loadMoreBtn.addEventListener('click', this.handleLoadMore.bind(this));
    
    // Handle browser back/forward buttons
    window.addEventListener('popstate', this.handlePopState.bind(this));
  }

  async handleLoadMore() {
    const nextUrl = this.loadMoreBtn.dataset.nextUrl;
    const currentPage = parseInt(this.loadMoreBtn.dataset.currentPage);
    const totalPages = parseInt(this.loadMoreBtn.dataset.totalPages);
    
    if (!nextUrl) return;
    
    // Show loading state
    this.setLoadingState(true);
    
    try {
      const response = await fetch(nextUrl);
      if (!response.ok) throw new Error('Network response was not ok');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract new products
      const newProductList = doc.querySelector('product-list');
      if (newProductList) {
        // Append new products to existing list
        const newProducts = Array.from(newProductList.children);
        
        // Set up stagger animation for new products if stagger is enabled
        if (window.themeVariables?.settings?.staggerProducts) {
          newProducts.forEach(product => {
            if (product.tagName === 'PRODUCT-CARD') {
              product.setAttribute('reveal-on-scroll', 'true');
              // Set initial hidden state for animation
              product.style.opacity = '0';
              product.style.transform = 'translateY(20px)';
            }
          });
        } else {
          // If stagger is disabled, ensure products are visible
          newProducts.forEach(product => {
            if (product.tagName === 'PRODUCT-CARD') {
              product.style.opacity = '1';
              product.style.transform = 'translateY(0)';
            }
          });
        }
        
        newProducts.forEach(product => {
          this.productList.appendChild(product);
        });
        
        // Trigger stagger animation for newly loaded products
        if (window.themeVariables?.settings?.staggerProducts && newProducts.length > 0) {
          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            this.animateNewProducts(newProducts);
          });
        }
      }
      
      // Update button state
      const nextPage = currentPage + 1;
      this.loadMoreBtn.dataset.currentPage = nextPage;
      
      // Check if there are more pages
      const nextPageUrl = this.getNextPageUrl(nextUrl, nextPage + 1);
      if (nextPage < totalPages && nextPageUrl) {
        this.loadMoreBtn.dataset.nextUrl = nextPageUrl;
      } else {
        // No more pages, hide button
        this.loadMoreBtn.style.display = 'none';
      }
      
      // Update URL without page reload
      const newUrl = new URL(nextUrl, window.location.origin);
      newUrl.searchParams.set('page', nextPage);
      window.history.pushState({ page: nextPage }, '', newUrl.pathname + newUrl.search);
      
      // Update page title if needed
      document.title = doc.title;
      
    } catch (error) {
      console.error('Error loading more products:', error);
      this.showError();
    } finally {
      this.setLoadingState(false);
    }
  }
  
  setLoadingState(isLoading) {
    const loadText = this.loadMoreBtn.querySelector('.load-more-text');
    const loadLoader = this.loadMoreBtn.querySelector('.load-more-loader');
    
    if (isLoading) {
      this.loadMoreBtn.disabled = true;
      loadText.classList.add('hidden');
      loadLoader.classList.remove('hidden');
    } else {
      this.loadMoreBtn.disabled = false;
      loadText.classList.remove('hidden');
      loadLoader.classList.add('hidden');
    }
  }
  
  getNextPageUrl(currentUrl, nextPage) {
    // Handle both absolute and relative URLs
    const url = new URL(currentUrl, window.location.origin);
    url.searchParams.set('page', nextPage);
    // Return relative URL to match Shopify's format
    return url.pathname + url.search;
  }
  
  showError() {
    const errorMessage = document.createElement('div');
    errorMessage.className = 'collection-load-error';
    errorMessage.innerHTML = `
      <p>${window.translations.load_more_error}</p>
      <button onclick="this.parentElement.remove(); window.location.reload();">
        ${window.translations.try_again}
      </button>
    `;
    this.loadMoreBtn.parentNode.appendChild(errorMessage);
    this.loadMoreBtn.style.display = 'none';
  }
  
  animateNewProducts(newProducts) {
    const productCards = newProducts.filter(product => product.tagName === 'PRODUCT-CARD');
    
    console.log('Animating new products:', productCards.length); // Debug log
    
    if (productCards.length === 0) return;

    // Skip the ProductList.reveal() method as it affects all products, not just new ones
    // Skip global animation functions that might affect existing products
    
    // Use manual stagger animation that only targets new products
    console.log('Using targeted animation for new products only'); // Debug log
    productCards.forEach((product, index) => {
      // Calculate stagger delay
      const delay = 100 + (index * 50); // 100ms start delay + 50ms per item
      
      setTimeout(() => {
        product.style.transition = 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out';
        product.style.opacity = '1';
        product.style.transform = 'translateY(0)';
        
        // Clean up the transition after animation completes
        setTimeout(() => {
          product.style.transition = '';
        }, 300);
      }, delay);
    });
  }

  handlePopState(event) {
    // Handle browser back button - you might want to reload the page
    // or implement more sophisticated state management
    window.location.reload();
  }

  async handleInitialPageLoad() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get('page')) || 1;
    
    console.log('Current page from URL:', currentPage);
    
    // If we're on page 2 or higher, load previous pages
    if (currentPage > 1 && this.productList) {
      // Get total pages from load more button if it exists, otherwise estimate
      let totalPages = currentPage; // Default assumption
      if (this.loadMoreBtn && this.loadMoreBtn.dataset.totalPages) {
        totalPages = parseInt(this.loadMoreBtn.dataset.totalPages);
      }
      
      console.log('Loading pages 1 through', currentPage, 'of', totalPages);
      
      // Show loading state if button exists
      if (this.loadMoreBtn) {
        this.setLoadingState(true);
      }
      
      try {
        // Clear existing products
        this.productList.innerHTML = '';
        
        // Load pages 1 through current page
        for (let page = 1; page <= currentPage; page++) {
          const pageUrl = this.getPageUrl(page);
          console.log('Loading page', page, 'from', pageUrl);
          await this.loadPageProducts(pageUrl, page, page === currentPage);
        }
        
        // Update load more button for next page if it exists
        if (this.loadMoreBtn) {
          const nextPage = currentPage + 1;
          if (nextPage <= totalPages) {
            const nextPageUrl = this.getPageUrl(nextPage);
            this.loadMoreBtn.dataset.nextUrl = nextPageUrl;
            this.loadMoreBtn.dataset.currentPage = currentPage;
          } else {
            this.loadMoreBtn.style.display = 'none';
          }
        }
        
      } catch (error) {
        console.error('Error loading initial pages:', error);
        // Fallback: reload the page normally
        window.location.reload();
      } finally {
        if (this.loadMoreBtn) {
          this.setLoadingState(false);
        }
      }
    }
  }

  async loadPageProducts(pageUrl, pageNumber, shouldAnimate = false) {
    const response = await fetch(pageUrl);
    if (!response.ok) throw new Error(`Failed to load page ${pageNumber}`);
    
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const newProductList = doc.querySelector('product-list');
    if (newProductList && this.productList) {
      const newProducts = Array.from(newProductList.children);
      
      // Set up animation state for new products
      if (window.themeVariables?.settings?.staggerProducts && shouldAnimate) {
        newProducts.forEach(product => {
          if (product.tagName === 'PRODUCT-CARD') {
            product.setAttribute('reveal-on-scroll', 'true');
            product.style.opacity = '0';
            product.style.transform = 'translateY(20px)';
          }
        });
      } else {
        // Ensure products are visible if no animation
        newProducts.forEach(product => {
          if (product.tagName === 'PRODUCT-CARD') {
            product.style.opacity = '1';
            product.style.transform = 'translateY(0)';
          }
        });
      }
      
      // Append products to list
      newProducts.forEach(product => {
        this.productList.appendChild(product);
      });
      
      // Animate if this is the last page being loaded
      if (shouldAnimate && window.themeVariables?.settings?.staggerProducts && newProducts.length > 0) {
        requestAnimationFrame(() => {
          this.animateNewProducts(newProducts);
        });
      }
    }
  }

  getPageUrl(pageNumber) {
    const url = new URL(window.location);
    if (pageNumber === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', pageNumber);
    }
    return url.pathname + url.search;
  }
}

// Expose class globally for facets integration
window.CollectionLoadMore = CollectionLoadMore;

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Initialize if we have either a load more button OR we're on a collection page with products
  if (document.getElementById('load-more-btn') || document.getElementById('collection-products-container')) {
    new CollectionLoadMore();
  }
});